<template>
  <div v-if="visible && !isProduction" class="mobile-console">
    <div class="console-header">
      <div class="console-title">控制台日志</div>
      <div class="console-actions">
        <span class="action-btn" @click="clearLogs">清空</span>
        <span class="action-btn" @click="toggleConsole">关闭</span>
      </div>
    </div>
    <div class="console-body" ref="consoleBody">
      <div
        v-for="(log, index) in logs"
        :key="index"
        :class="['log-item', `log-${log.type}`]"
      >
        <span class="log-time">{{ log.time }}</span>
        <span class="log-type">{{ log.type.toUpperCase() }}</span>
        <div class="log-content">{{ log.message }}</div>
      </div>
      <div v-if="logs.length === 0" class="empty-logs">暂无日志</div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
// MobileConsole 组件需要拦截 console，所以允许使用 console
export default {
  name: 'MobileConsole',
  data () {
    return {
      visible: false,
      logs: [],
      maxLogs: 200,
      isProduction: process.env.NODE_ENV === 'production',
      originalConsole: {
        log: console.log,
        error: console.error,
        warn: console.warn,
        info: console.info
      }
    }
  },
  mounted () {
    // 生产环境不启用控制台功能
    if (this.isProduction) {
      return
    }
    this.interceptConsole()
    this.initGesture()
  },
  beforeDestroy () {
    // 生产环境不需要恢复
    if (this.isProduction) {
      return
    }
    this.restoreConsole()
  },
  methods: {
    interceptConsole () {
      // 生产环境不拦截console
      if (this.isProduction) {
        return
      }

      const self = this

      console.log = function (...args) {
        self.originalConsole.log.apply(console, args)
        self.addLog('log', args)
      }

      console.error = function (...args) {
        self.originalConsole.error.apply(console, args)
        self.addLog('error', args)
      }

      console.warn = function (...args) {
        self.originalConsole.warn.apply(console, args)
        self.addLog('warn', args)
      }

      console.info = function (...args) {
        self.originalConsole.info.apply(console, args)
        self.addLog('info', args)
      }
    },

    restoreConsole () {
      console.log = this.originalConsole.log
      console.error = this.originalConsole.error
      console.warn = this.originalConsole.warn
      console.info = this.originalConsole.info
    },

    addLog (type, args) {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2)
          } catch (e) {
            return String(arg)
          }
        }
        return String(arg)
      }).join(' ')

      const time = new Date().toLocaleTimeString()

      this.logs.push({
        type,
        message,
        time
      })

      // 限制日志数量
      if (this.logs.length > this.maxLogs) {
        this.logs.shift()
      }

      // 自动滚动到底部
      this.$nextTick(() => {
        if (this.$refs.consoleBody) {
          this.$refs.consoleBody.scrollTop = this.$refs.consoleBody.scrollHeight
        }
      })
    },

    clearLogs () {
      this.logs = []
    },

    toggleConsole () {
      this.visible = !this.visible
    },

    initGesture () {
      // 生产环境不初始化手势
      if (this.isProduction) {
        return
      }

      // 双击屏幕右下角区域打开/关闭控制台
      let tapCount = 0
      let tapTimer = null

      document.addEventListener('touchstart', (e) => {
        const touch = e.touches[0]
        const screenWidth = window.innerWidth
        const screenHeight = window.innerHeight

        // 检测是否点击右下角区域（右下角100x100区域）
        if (touch.clientX > screenWidth - 100 && touch.clientY > screenHeight - 100) {
          tapCount++

          if (tapCount === 1) {
            tapTimer = setTimeout(() => {
              tapCount = 0
            }, 300)
          } else if (tapCount === 2) {
            clearTimeout(tapTimer)
            tapCount = 0
            this.toggleConsole()
          }
        } else {
          tapCount = 0
          if (tapTimer) {
            clearTimeout(tapTimer)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.mobile-console {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  font-family: 'Courier New', monospace;
  color: #fff;
}

.console-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.console-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}

.console-actions {
  display: flex;
  gap: 15px;
}

.action-btn {
  font-size: 12px;
  color: #4a9eff;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.action-btn:active {
  background: rgba(74, 158, 255, 0.2);
}

.console-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  font-size: 12px;
  line-height: 1.6;
}

.log-item {
  margin-bottom: 8px;
  padding: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  word-break: break-all;
}

.log-item.log-error {
  background: rgba(255, 59, 48, 0.2);
  border-left: 3px solid #ff3b30;
}

.log-item.log-warn {
  background: rgba(255, 204, 0, 0.2);
  border-left: 3px solid #ffcc00;
}

.log-item.log-info {
  background: rgba(0, 122, 255, 0.2);
  border-left: 3px solid #007aff;
}

.log-time {
  display: inline-block;
  color: #888;
  font-size: 10px;
  margin-right: 8px;
}

.log-type {
  display: inline-block;
  font-size: 10px;
  font-weight: bold;
  margin-right: 8px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
}

.log-item.log-error .log-type {
  color: #ff3b30;
}

.log-item.log-warn .log-type {
  color: #ffcc00;
}

.log-item.log-info .log-type {
  color: #007aff;
}

.log-content {
  margin-top: 4px;
  white-space: pre-wrap;
  color: #e0e0e0;
}

.empty-logs {
  text-align: center;
  color: #666;
  padding: 40px 20px;
  font-size: 14px;
}

/* 滚动条样式 */
.console-body::-webkit-scrollbar {
  width: 4px;
}

.console-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.console-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.console-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>
